import { useEffect, useState, useCallback } from "react";
import { BETA_MODE, BETA_BANNER_TEXT } from "@/lib/betaMode";
import { useIsAdmin } from "@/hooks/useIsAdmin";

/**
 * useEffectiveBetaMode — like BETA_MODE but lets admins bypass it for UI testing.
 *
 * - Non-admins (and signed-out visitors): always see beta UI while BETA_MODE is true.
 * - Admins: by default see the real paid-plan UI (bypass), but can toggle a
 *   sessionStorage flag ("admin-preview-beta") to temporarily re-enable beta UI
 *   for side-by-side comparison. The toggle is per-tab and resets on tab close.
 *
 * SEO/structured data still uses the raw BETA_MODE constant.
 */
const STORAGE_KEY = "admin-preview-beta";
const EVENT_NAME = "admin-preview-beta-changed";

const readPreview = () => {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(STORAGE_KEY) === "1";
};

export const useEffectiveBetaMode = () => {
  const { isAdmin, loading } = useIsAdmin();
  const [adminPreviewBeta, setAdminPreviewBeta] = useState<boolean>(readPreview);

  useEffect(() => {
    const handler = () => setAdminPreviewBeta(readPreview());
    window.addEventListener(EVENT_NAME, handler);
    // 'storage' fires in OTHER tabs when localStorage changes — keeps tabs in sync.
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener(EVENT_NAME, handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  const setPreview = useCallback((on: boolean) => {
    if (on) localStorage.setItem(STORAGE_KEY, "1");
    else localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new Event(EVENT_NAME));
  }, []);

  const toggleAdminPreviewBeta = useCallback(() => {
    setPreview(!readPreview());
  }, [setPreview]);

  // Admin sees beta UI only if they explicitly opted in via the toggle.
  const effectiveBetaMode = BETA_MODE && (!isAdmin || adminPreviewBeta);

  return {
    effectiveBetaMode,
    isAdmin,
    loading,
    adminPreviewBeta: isAdmin && adminPreviewBeta,
    toggleAdminPreviewBeta,
  };
};

export { BETA_BANNER_TEXT };
